import React from "react";
import { useTranslation } from "react-i18next";
import { PlannerData } from "../../hooks/useGenericPlanner";
import { usePlanningConfiguration } from "../../hooks/usePlanningConfiguration";
import Datatable from "../../../../../components/Common/dataTable";
import "./GenericPlanner.css";
import { getSubjectCodeFromkey } from "utils/schoolUtils";

interface GenericPlannerGridProps {
  role: string;
  data: PlannerData[];
}

const toMinutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const normalize = (ts: string) => ts.slice(0, 5);

const GenericPlannerGrid: React.FC<GenericPlannerGridProps> = ({ role, data }) => {
  const { t } = useTranslation();
  const { config, loading } = usePlanningConfiguration();
  if (loading) return <p>{t("loading")}</p>;

  const { days, timeSlots } = config;

  const dataSource = days?.map((day) => {
    const dayData = data.filter((d) => String(d.day) === day.value);
    const record: any = { key: day.value, day: t(day.label) };

    timeSlots.forEach((slot) => {
      const slotStart = normalize(slot.start);
      const slotEnd = normalize(slot.end);
      const slotStartMin = toMinutes(slotStart);
      const slotEndMin = toMinutes(slotEnd);

      const entriesInSlot = dayData.filter((d) => {
        const entryStartMin = toMinutes(d.startTime);
        const entryEndMin = toMinutes(d.endTime);
        // intersect avec le créneau
        return entryStartMin < slotEndMin && entryEndMin > slotStartMin;
      });

      record[slot.start] = entriesInSlot; // tableau (vide ou non)
    });

    return record;
  });

  const renderContent = (entry: PlannerData) => (
    <div className="planner-pill" style={{ background: entry.subjectColor }}>
      {role === "teacher" ? (
        <>
          <div className="planner-pill-row">
            <i className="ti ti-group planner-pill-icon" />
            <span className="planner-pill-text">
              {entry?.className}
            </span>
          </div>

          <div className="planner-pill-row">
            <i className="ti ti-book planner-pill-icon" />
            <span className="planner-pill-text">
              {getSubjectCodeFromkey(entry?.subjectCode)}
            </span>
          </div>

          {/* Time */}
          <div className="planner-pill-row planner-pill-time">
            <i className="ti ti-clock planner-pill-icon" />
            <span>
              {entry.startTime} – {entry.endTime}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="planner-pill-row">
            <i className="ti ti-book planner-pill-icon" />
            <span className="planner-pill-text">
              {getSubjectCodeFromkey(entry?.subjectCode)}
            </span>
          </div>

          <div className="planner-pill-row planner-pill-time">
            <i className="ti ti-clock planner-pill-icon" />
            <span>
              {entry.startTime} – {entry.endTime}
            </span>
          </div>

          <div className="planner-pill-teacher">
            {entry.teacherPhoto ? (
              <img
                src={entry.teacherPhoto}
                alt={entry.teacherName}
                className="planner-teacher-avatar"
              />
            ) : (
              <div className="planner-teacher-avatar placeholder">
                {entry.teacherName
                  ?.split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
            )}
            <span className="planner-teacher-name">
              {entry.teacherName}
            </span>
          </div>
        </>
      )}
    </div>

  );

  const columns = [
    {
      title: t("day"),
      dataIndex: "day",
      key: "day",
      fixed: "left" as const,
      width: 120,
      className: "day-column",
    },
    ...(timeSlots?.map((slot) => {
      const slotStart = normalize(slot.start);
      const slotEnd = normalize(slot.end);

      return {
        title: slot.label || `${slotStart} – ${slotEnd}`,
        dataIndex: slot.start,
        key: slot.start,
        width: 200, 
        className: "time-column",
        render: (entries: PlannerData[]) => {
          if (!entries || entries.length === 0) {
            return <div className="noScheduleCell">-</div>;
          }

          return (
            <div className="stacked-cell">
              {entries.map((entry, idx) => (
                <div key={idx} className="stacked-cell-item">
                  {renderContent(entry)}
                </div>
              ))}
            </div>
          );
        },
      };
    }) || []),
  ];

  return (
    <div className="planner-datatable-wrapper">
      <Datatable
        columns={columns}
        dataSource={dataSource}
        Selection={false}
        bordered={true}
        pagination={false}
      />
    </div>
  );
};

export default GenericPlannerGrid;
